// DNDContext.jsx
import React, { createContext, useReducer, useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import { v4 as uuidv4 } from 'uuid';

export const DNDContext = createContext();

const dropStrategies = new Map();

export const registerDropStrategy = (type, handler) => {
	dropStrategies.set(type, handler);
};

export const forceTopInsertion = (result, state) => {
	const destinationColumn = state.columns.find((col) => col.id === result.destination?.droppableId);

	if (!destinationColumn) return result;

	if (!destinationColumn.reorderable) {
		return {
			...result,
			destination: {
				...result.destination,
				index: 0,
			},
		};
	}

	return result;
};

export const defaultDropStrategy = (result, state, dispatch) => {
	const { destination, source, draggableId } = result;
	const { columns } = state;
	const callback = (columns) => dispatch({ type: 'MERGE_COLUMNS', columns: columns });

	if (!destination || !source) return;

	dispatch({
		type: 'SET_DROP_FLAGS',
		dropsDisabled: false,
		disablePlaceholder: false,
		showDropZones: false,
	});
	dispatch({ type: 'SET_CHANGE_MADE', changeMade: true });

	const colStartIndex = columns.findIndex((col) => col.id === source.droppableId);
	const colFinishIndex = columns.findIndex((col) => col.id === destination.droppableId);

	const columnStart = columns[colStartIndex];
	const columnFinish = columns[colFinishIndex];

	if (!columnStart || !columnFinish) return;

	const dragItemIndex = columnStart.fields.findIndex((f) => f.id === draggableId);
	const draggedField = columnStart.fields[dragItemIndex];

	if (!draggedField) return;
	if (columnStart.id === columnFinish.id) {
		return;
	}

	const newStartFields = [...columnStart.fields];
	if (!columnStart.immutable) {
		newStartFields.splice(source.index, 1);
	}

	const newDragItem = { ...draggedField };

	const newFinishFields = [...columnFinish.fields];
	if (!columnFinish.immutable) {
		newFinishFields.splice(destination.index, 0, newDragItem);
	}

	const updatedColumns = columns;
	updatedColumns[colStartIndex] = { ...columnStart, fields: newStartFields };
	updatedColumns[colFinishIndex] = { ...columnFinish, fields: newFinishFields };

	if (callback) callback(updatedColumns);
};

const initialState = {
	dropsDisabled: false,
	disablePlaceholder: false,
	showDropZones: false,
	changeMade: false,
	sidebarOpen: false,
	dropType: 'default',
	onDragStartExtended: null,
	onDragEndExtended: null,
	columns: [],
};

// {
// 	id: 'column_sidebar_order_accompaniments',
// 	type: 'accompaniment',
// 	fields: [{ id: '1221', orderRef: 'ABC-123', title: 'Wets', description: 'Very nice wets' }],
// 	immutable: false,
// },
// {
// 	id: 'column_package_items',
// 	type: 'item',
// 	fields: [],
// 	immutable: false,
// },

function reducer(state, action) {
	switch (action.type) {
		case 'SET_DND_CALLBACKS':
			return {
				...state,
				onDragStartExtended: action.onDragStartExtended,
				onDragEndExtended: action.onDragEndExtended,
			};
		case 'RESET_DND_STATE':
			return {
				...initialState,
				onDragStartExtended: state.onDragStartExtended,
				onDragEndExtended: state.onDragEndExtended,
			};
		case 'SET_DROP_FLAGS':
			return {
				...state,
				dropsDisabled: action.dropsDisabled,
				disablePlaceholder: action.disablePlaceholder,
				showDropZones: action.showDropZones,
			};
		case 'MERGE_COLUMNS': {
			const newColumns = action.columns;

			const updatedColumns = [...state.columns];
			newColumns.forEach((newCol) => {
				if (!newCol) {
					return;
				}
				const existingIndex = updatedColumns.findIndex((c) => c.id === newCol.id);
				if (existingIndex !== -1) {
					updatedColumns[existingIndex] = newCol;
				} else {
					updatedColumns.push(newCol);
				}
			});
			return {
				...state,
				columns: updatedColumns,
			};
		}
		case 'REPLACE_COLUMNS_BY_REGION': {
			const { region, columns: newRegionColumns } = action;

			const filtered = state.columns.filter((col) => col.region !== region);

			return {
				...state,
				columns: [...filtered, ...newRegionColumns],
			};
		}
		case 'REMOVE_COLUMN': {
			const idToRemove = action.columnId;
			return {
				...state,
				columns: state.columns.filter((col) => col.id !== idToRemove),
			};
		}
		case 'SET_FIELDS':
			return {
				...state,
				fields: action.fields,
			};
		case 'SET_SELECTED':
			return {
				...state,
				selected: action.selected,
			};
		case 'SET_CHANGE_MADE':
			return {
				...state,
				changeMade: action.changeMade,
			};
		case 'SET_DROP_TYPE':
			return { ...state, dropType: action.dropType };
		default:
			return state;
	}
}

export function DNDContextProvider({ children }) {
	const [DNDState, DNDDispatch] = useReducer(reducer, initialState);

	const onDragStart = ({ draggableId }) => {
		if (draggableId.startsWith('container'))
			DNDDispatch({
				type: 'SET_DROP_FLAGS',
				dropsDisabled: true,
				disablePlaceholder: false,
				showDropZones: true,
			});
		else if (draggableId.startsWith('template'))
			DNDDispatch({
				type: 'SET_DROP_FLAGS',
				dropsDisabled: false,
				disablePlaceholder: true,
				showDropZones: true,
			});
		else
			DNDDispatch({
				type: 'SET_DROP_FLAGS',
				dropsDisabled: false,
				disablePlaceholder: false,
				showDropZones: true,
			});

		if (typeof DNDState.onDragStartExtended === 'function') {
			DNDState.onDragStartExtended({ draggableId });
		}
	};

	const onDragEnd = async (result) => {
		DNDDispatch({ type: 'SET_DROP_FLAGS', dropsDisabled: false, disablePlaceholder: false, showDropZones: false });
		DNDDispatch({ type: 'SET_CHANGE_MADE', changeMade: true });

		const handler = dropStrategies.get(DNDState.dropType);

		if (typeof DNDState.onDragEndExtended === 'function') {
			let success = await DNDState.onDragEndExtended(result);

			if (success === false) {
				return;
			}
		}

		if (handler) {
			const processedResult = forceTopInsertion(result, DNDState);
			handler(processedResult, DNDState, DNDDispatch);
		}
	};

	return (
		<DNDContext.Provider value={{ DNDState, DNDDispatch }}>
			<DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
				{children}
			</DragDropContext>
		</DNDContext.Provider>
	);
}
